import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactDOM from 'react-dom';
import { useOutletContext } from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Box, IconButton, Tooltip } from '@mui/material';
import { MergeType as MergeIcon } from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import Fader from '../Loaders/Fader';

import { TABLE_THEME, USER_ROLES } from '../../../util/constants/AppConstants';
import { mergeConstituent } from '../../../http/constituentAPI';
import { setNotify } from '../../../datastore/actions/actionActions';
import useOutsideClick from '../../../hooks/useOutsideClick';

import CloseIcon from '../../../assets/images/icons/close.svg';
import './MatchingConstituents.scss';

const MatchingConstituents = ({
  isOpen,
  onCloseHandler,
  activeConstituent,
  constituents,
  constituentsLoading,
  handleGetConstituentsData,
}) => {
  const [saving, setSaving] = useState(false);
  const [wrapperHeight, setWrapperHeight] = useState(0);

  const dispatch = useDispatch();

  const config = useSelector((state) => state.config.config);

  const windowRef = useRef();

  const { userInfo } = useOutletContext();

  const mppRoleValue = USER_ROLES.find((role) => role.short === 'MPP').value;
  const councillorRoleValue = USER_ROLES.find((role) => role.short === 'Councillor').value;

  const isMpp = userInfo && userInfo.role === mppRoleValue;
  const isCouncillor = userInfo && userInfo.role === councillorRoleValue;

  useEffect(() => {
    const calculateHeight = () => {
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      let height;

      if (vw <= 280) {
        height = vh * 0.85 - 172;
      } else if (vw <= 420) {
        height = vh * 0.85 - 156;
      } else if (vw <= 550) {
        height = vh * 0.85 - 124;
      } else if (vw <= 1024) {
        height = vh * 0.9 - 156;
      } else {
        height = vh * 0.9 - 140;
      }

      setWrapperHeight(Math.floor(height));
    };

    calculateHeight();
    window.addEventListener('resize', calculateHeight);
    return () => window.removeEventListener('resize', calculateHeight);
  }, []);

  const handleMergeConstituent = async (e, id) => {
    e.stopPropagation();

    setSaving(true);

    const res = await mergeConstituent(config, userInfo.id, id, activeConstituent.id);
    if (res.status === 200) {
      dispatch(
        setNotify({
          status: true,
          type: 'success',
          title: 'Success',
          message: res.data.message,
        }),
      );

      handleGetConstituentsData(userInfo.id);
      onCloseHandler();
      setSaving(false);
    } else {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: res.data.message,
        }),
      );
    }

    setSaving(false);
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorKey: 'name',
        header: 'Name',
        minSize: 110,
        grow: 1,
        enableSorting: false,
      },
      {
        accessorKey: 'contact_count',
        header: 'Contact count',
        minSize: 110,
        grow: 1,
        enableSorting: true,
        filterVariant: 'range',
      },
      {
        accessorFn: (row) => {
          const pollNumber = row.poll_label;
          return pollNumber ? `Poll - ${pollNumber}` : null;
        },
        id: 'poll_label',
        header: 'Poll',
        minSize: 110,
        grow: 1,
        filterVariant: 'select',
      },
      {
        accessorKey: 'postal_code',
        header: 'Postal code',
        minSize: 110,
        grow: 1,
        enableSorting: false,
      },
      {
        accessorKey: 'address',
        header: 'Address',
        minSize: 150,
        grow: 5,
        enableSorting: false,
      },
      {
        accessorKey: 'email',
        header: 'Email',
        minSize: 110,
        grow: 1,
        enableSorting: false,
      },
      {
        accessorKey: 'phone',
        header: 'Phone',
        minSize: 110,
        grow: 1,
        enableSorting: false,
      },
    ];
    return baseColumns;
  }, []);

  const theme = createTheme(TABLE_THEME);

  const memoColumns = useMemo(() => columns, [columns]);
  const memoData = useMemo(() => constituents, [constituents]);

  const table = useMaterialReactTable({
    columns: memoColumns,
    data: memoData,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableColumnResizing: true,
    enableColumnFilters: true,
    enableFacetedValues: true,
    getRowId: (originalRow) => originalRow.id,
    state: { isLoading: constituentsLoading },
    positionToolbarAlertBanner: 'none',
    muiPaginationProps: {
      color: 'primary',
      shape: 'rounded',
      showRowsPerPage: false,
      showFirstButton: true,
      showLastButton: true,
    },
    paginationDisplayMode: 'pages',
    initialState: {
      columnOrder: ['name', 'contact_count', 'poll_label', 'postal_code', 'address', 'email', 'phone'],
      pagination: { pageSize: 10, pageIndex: 0 },
      showColumnFilters: true,
    },
    muiTableBodyCellProps: {
      sx: {
        marginRight: '12px',
        color: '#343434',
        fontSize: '14px',
        fontWeight: 'normal',
        border: 'none',
        whiteSpace: 'nowrap',
        overflowX: 'hidden',
        textOverflow: 'ellipsis',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        marginRight: '12px',
        color: '#343434',
        fontSize: '16px',
        fontWeight: 500,
        border: 'none',
      },
    },
    muiTableHeadRowProps: {
      sx: {
        borderTop: '1px solid #D3D3D3',
        borderBottom: '1px solid #D3D3D3',
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        window.open(`/constituent/${row.original.id}`, '_blank');
      },
      sx: {
        cursor: row.original.pending ? 'default' : 'pointer',
        borderBottom: '1px solid #D3D3D3',
        backgroundColor: '#FFFFFF',
        '&:hover': {
          backgroundColor: '#F7F7F7',
        },
      },
    }),
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex', flexWrap: 'nowrap' }}>
        <Tooltip title="Merge">
          <IconButton
            className={`icon-button ${isMpp || isCouncillor ? 'blue' : ''}`}
            onClick={(e) => {
              handleMergeConstituent(e, row.original.id);
            }}
          >
            <MergeIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    displayColumnDefOptions: {
      'mrt-row-actions': {
        size: 100,
        grow: false,
        muiTableHeadCellProps: {
          sx: {
            fontSize: '16px',
            fontWeight: 500,
            position: 'sticky',
            right: 0,
            zIndex: 10,
            border: 'none',
            boxShadow: '-4px 0 8px -2px rgba(0, 0, 0, 0.1) !important',
          },
        },
        muiTableBodyCellProps: {
          sx: {
            position: 'sticky',
            right: 0,
            zIndex: 10,
            border: 'none',
            boxShadow: '-4px 0 8px -2px rgba(0, 0, 0, 0.1) !important',
          },
        },
      },
    },
  });

  useOutsideClick(windowRef, null, isOpen, onCloseHandler);

  return ReactDOM.createPortal(
    <div className="login-wrap feedback">
      <div className={`login-view ${saving ? 'saving' : ''}`}>
        <div ref={windowRef} className="login-box matching-constituents__box">
          {!saving ? (
            <>
              <img src={CloseIcon} onClick={onCloseHandler} className="close-icon" />
              <h2 className="matching-constituents__title">Merge Constituent</h2>
              <p className="matching-constituents__text">
                The system has identified constituents with the same name as the pending constituent{' '}
                <span>{activeConstituent ? activeConstituent.name : ''}</span>.
              </p>
              <p className="matching-constituents__text">
                It can be merged into the selected constituent from the list.
              </p>
              <div className="table__wrapper" style={{ height: `${wrapperHeight}px` }}>
                <PerfectScrollbar
                  options={{
                    wheelPropagation: false,
                    autoHide: false,
                  }}
                >
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <ThemeProvider theme={theme}>
                      <div className="table">
                        <MaterialReactTable table={table} />
                      </div>
                    </ThemeProvider>
                  </LocalizationProvider>
                </PerfectScrollbar>
              </div>
            </>
          ) : (
            <>
              <div className="info__title">Merging Constituent</div>
              <div className="info__text">The process should take no longer than 30 seconds. Please Wait...</div>
              <Fader size={50} />
            </>
          )}
        </div>
      </div>
    </div>,
    document.getElementById('root'),
  );
};

export default MatchingConstituents;
